import { useState } from "react";
import { CheckCircle2, KeyRound, Loader2, ShieldCheck } from "lucide-react";
import { fmtDate, loginBF, registerBF, vid } from "@/lib/bf";
import BFGdprNotice, { GDPR_VERSION } from "./BFGdprNotice";
import Countdown from "./Countdown";

export const isRegistered = () => localStorage.getItem("bf_reg") === GDPR_VERSION && !!localStorage.getItem("bf_lead_id");
const regInfo = () => JSON.parse(localStorage.getItem("bf_reg_info") || "null");
export const saveReg = (id, info) => {
  localStorage.setItem("bf_lead_id", id);
  localStorage.setItem("bf_reg_info", JSON.stringify(info));
  localStorage.setItem("bf_reg", GDPR_VERSION);
};
const errText = (x, def) => { const d = x.response?.data?.detail; return typeof d === "string" ? d : def; };

const FIELDS = [
  ["salon", "Nume salon", "text", 80], ["admin_name", "Nume administrator", "text", 80], ["cui", "CUI firmă (ex: RO12345678)", "text", 14],
  ["address", "Adresă de livrare (stradă, număr, oraș, județ)", "text", 200], ["postal", "Cod poștal (6 cifre)", "text", 6], ["email", "E-mail", "email", 120], ["phone", "Telefon (WhatsApp)", "tel", 30],
];

const LoginForm = ({ onDone, onBack }) => {
  const [f, setF] = useState({ cui: "", phone: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const submit = (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    loginBF(f).then((r) => { const { id, ...info } = r; saveReg(id, info); onDone(); })
      .catch((x) => { setErr(errText(x, "Nu am putut verifica datele")); setBusy(false); });
  };
  return (
    <form className="bf-gate-card" onSubmit={submit} data-testid="bf-login-form">
      <h2><KeyRound size={22} /> Intră cu salonul deja înregistrat</h2>
      <p className="bf-gate-sub">Folosește CUI-ul și telefonul cu care ai înregistrat salonul. Nu ai nevoie de parolă.</p>
      <input placeholder="CUI firmă (ex: RO12345678)" value={f.cui} onChange={(e) => setF({ ...f, cui: e.target.value })} maxLength={14} required data-testid="bf-login-cui" />
      <input type="tel" placeholder="Telefon (WhatsApp)" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} maxLength={30} required data-testid="bf-login-phone" />
      {err && <p className="bf-lead-err" data-testid="bf-login-error">{err}</p>}
      <button type="submit" disabled={busy} data-testid="bf-login-submit">{busy && <Loader2 size={16} className="animate-spin" />} Intră</button>
      <button type="button" className="bf-gate-switch" onClick={onBack} data-testid="bf-login-back">Salon nou? Înregistrează-l aici</button>
    </form>
  );
};

const Done = ({ cfg }) => (
  <div className="bf-gate-card bf-gate-done" data-testid="bf-soon-registered">
    <h2><CheckCircle2 size={22} /> {regInfo()?.salon || "Salonul"} este înscris</h2>
    <p className="bf-gate-sub">Pe {fmtDate(cfg.start)}, ora {cfg.start_time}, deschizi același link și intri direct în oferte, fără să mai completezi nimic. De pe alt telefon intri cu CUI-ul și telefonul salonului.</p>
  </div>
);

export default function BFGdprGate({ cfg, onDone, preview, soon }) {
  const [f, setF] = useState({ salon: "", admin_name: "", cui: "", address: "", postal: "", email: "", phone: "", gdpr: false, confidential: false, marketing: false, website: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [mode, setMode] = useState("reg");
  const [done, setDone] = useState(soon && isRegistered());
  const finish = () => (soon ? setDone(true) : onDone());
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const submit = (e) => {
    e.preventDefault();
    if (FIELDS.some(([k]) => !f[k].trim())) return setErr("Completează toate câmpurile");
    if (!/^\d{6}$/.test(f.postal.trim())) return setErr("Codul poștal are 6 cifre (ex: 010101)");
    if (!f.gdpr) return setErr("Bifează acordul GDPR ca să continui");
    if (!f.confidential) return setErr("Bifează angajamentul de confidențialitate ca să continui");
    setErr("");
    setBusy(true);
    registerBF({ ...f, postal: f.postal.trim(), visitor_id: vid(), agent: localStorage.getItem("bf_agent") || "" })
      .then((r) => { saveReg(r.id, { salon: f.salon, admin: f.admin_name, cui: f.cui, address: f.address, postal: f.postal.trim(), email: f.email, phone: f.phone }); finish(); })
      .catch((x) => { setErr(errText(x, "Verifică datele: e-mail, CUI și telefon")); setBusy(false); });
  };
  return (
    <div className={`bf-app bf-gate ${soon ? "bf-gate--soon" : ""}`} data-testid={soon ? "bf-soon" : "bf-gdpr-gate"}>
      <div className="bf-gate-wrap">
        {soon && (
          <header className="bf-soon-head" data-testid="bf-soon-head">
            <img src="/brands/vs_beautytrends_w.png" alt="VS Beauty Trends" className="bf-soon-vs" data-testid="bf-soon-logo" />
            <h1>Black Friday</h1>
            <p className="bf-sub">Începe pe {fmtDate(cfg.start)}, ora {cfg.start_time}</p>
            <Countdown cfg={cfg} />
          </header>
        )}
        {done ? <Done cfg={cfg} /> : mode === "login" ? <LoginForm onDone={finish} onBack={() => setMode("reg")} /> : (
          <form className="bf-gate-card" onSubmit={submit}>
            {!soon && <span className="bf-kicker">Beauty Trends · Black Friday 2026</span>}
            <h2><ShieldCheck size={22} /> {soon ? "Înregistrează salonul acum" : "Înregistrează salonul ca să vezi ofertele"}</h2>
            <p className="bf-gate-sub">{soon ? "Completezi o singură dată. La start intri direct în oferte, fără să mai completezi nimic." : "Datele ne ajută să-ți preluăm și să-ți livrăm comanda. Completezi o singură dată."}</p>
            <button type="button" className="bf-gate-switch" onClick={() => setMode("login")} data-testid="bf-gate-login-open">Salonul e deja înregistrat? Intră cu CUI și telefon</button>
            {FIELDS.map(([k, label, type, max]) => (
              <input key={k} type={type} inputMode={k === "postal" ? "numeric" : undefined} placeholder={label} value={f[k]} onChange={set(k)} maxLength={max} required autoComplete="on" data-testid={`bf-gate-${k}`} />
            ))}
            <input className="bf-hp" tabIndex={-1} autoComplete="off" aria-hidden="true" value={f.website} onChange={set("website")} />
            <details className="bf-gdpr" data-testid="bf-gdpr-details"><summary>Citește nota de informare GDPR</summary><BFGdprNotice cfg={cfg} /></details>
            <label className="bf-lead-consent"><input type="checkbox" checked={f.gdpr} onChange={set("gdpr")} data-testid="bf-gate-gdpr" />
              <span>Am citit nota de informare și sunt de acord cu prelucrarea datelor pentru preluarea, facturarea și livrarea comenzilor Black Friday. <b>(obligatoriu)</b></span></label>
            <label className="bf-lead-consent bf-conf" data-testid="bf-gate-confidential-label"><input type="checkbox" checked={f.confidential} onChange={set("confidential")} data-testid="bf-gate-confidential" />
              <span>Mă angajez să păstrez <b>confidențiale</b> ofertele, prețurile și condițiile Black Friday primite de la VS Beauty Trends. Sunt destinate exclusiv salonului meu și nu le transmit altor saloane, distribuitori sau firme concurente. <b>(obligatoriu)</b></span></label>
            <label className="bf-lead-consent"><input type="checkbox" checked={f.marketing} onChange={set("marketing")} data-testid="bf-gate-marketing" />
              <span>Vreau să primesc pe e-mail/WhatsApp oferte și noutăți Beauty Trends (opțional, pot renunța oricând).</span></label>
            {err && <p className="bf-lead-err" data-testid="bf-gate-error">{err}</p>}
            <button type="submit" disabled={busy} data-testid="bf-gate-submit">{busy && <Loader2 size={16} className="animate-spin" />} {soon ? "Semnez și mă înscriu" : "Semnez și văd ofertele"}</button>
            {preview && !soon && <button type="button" className="bf-gate-skip" onClick={onDone} data-testid="bf-gate-skip">Sari peste (doar în previzualizare)</button>}
          </form>
        )}
      </div>
    </div>
  );
}
